'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetcher } from '@/shared/utils/fetcher';
import { setTalentToken } from '@/shared/utils/talent-token';

const ENDPOINT = process.env.MODERN_TALENT_API_INTEGRATION;

type TUseTalentTokenInput = {
  role?: string | null;
  employeeId?: number | null;
  companyId?: number;
  userId?: number | null;
};

export const useTalentToken = ({
  role,
  employeeId,
  companyId,
  userId,
}: TUseTalentTokenInput) => {
  const resolvedEmployeeId = employeeId;
  const resolvedUserId =
    resolvedEmployeeId === null ? (userId ?? undefined) : undefined;

  const body = {
    employeeId: resolvedEmployeeId,
    role,
    companyId,
    userId: resolvedUserId,
  };

  const isEnabled =
    Boolean(resolvedEmployeeId) ||
    (resolvedEmployeeId === null &&
      Boolean(resolvedUserId) &&
      Boolean(companyId));

  const { data, isError, error, isPending, refetch } = useQuery({
    queryKey: ['talent-token', JSON.stringify(body)],
    queryFn: () =>
      fetcher<{ token: string }>(
        `${ENDPOINT}/learningintegration/security/generate-token-talent`,
        {
          method: 'POST',
          body: JSON.stringify(body),
          skipAuth: true,
        },
      ),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });

  if (isError) {
    console.error('Generate Talent Token error', error);
  }

  const token = typeof data === 'object' ? data.token : undefined;

  useEffect(() => {
    setTalentToken(token);
  }, [token]);

  if (token && typeof window !== 'undefined') {
    const browserWindow = window as typeof window & {
      crehApolloHeaders?: Record<string, string>;
    };

    browserWindow.crehApolloHeaders = {
      ...browserWindow.crehApolloHeaders,
      'talent-token': token,
    };
  }

  return {
    token,
    isPending,
    isError,
    error,
    /* CORE-1776: la query corre con `retry: false`, así que ante un fallo queda pegada
       hasta que alguien la reintente a mano. Exponemos `refetch` para que el botón de
       reintentar del error boundary global pueda recuperar la sesión sin refrescar. */
    refetch,
  };
};
